import { WeekdaySelectionDrawer } from '@/domain/termine/features/CreateTermin/Drawer/components/WeekdaySelectionDrawer';
import { OccurrenceCountFormField } from '@/domain/termine/features/CreateTermin/shared/FormFields/IndividuelleWiederholung/OccurrenceCountFormField';
import { WeekdayOccurrenceSelectFormField } from '@/domain/termine/features/CreateTermin/shared/FormFields/IndividuelleWiederholung/WeekdayOccurrenceSelectFormField';
import { DatePickerFormField } from '@/domain/termine/features/CreateTermin/shared/FormFields/DatePickerFormField';
import { RepeatIntervalSelectFormField } from '@/domain/termine/features/CreateTermin/shared/FormFields/IndividuelleWiederholung/RepeatIntervalSelectFormField';
import { useCreateTermineFormContext } from '@/domain/termine/features/CreateTermin/shared/useCreateTermineFormContext';
import {
  IndividuelleWiederholung,
  TerminWiederholungsIntervall,
} from '@/domain/termine/shared/types';
import {
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  ScrollArea,
} from '@e2n/ui';
import { useTranslation } from 'react-i18next';
import { getFixedWeekday } from '@/domain/termine/shared/utils/getWeekdaySelection';
import dayjs from 'dayjs';
import { IndividuellerWiederholungsText } from '@/domain/termine/shared/utils/IndividuellerWiederholungsText';

export function IndividualRepetitionDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const { form } = useCreateTermineFormContext();

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="flex flex-col">
        <ScrollArea className="flex-1 px-4 mt-4" data-vaul-no-drag>
          <div className="text-center text-sm font-semibold pb-2 px-4">
            {t('entities.wiederholung.individuell')}
          </div>
          <div className="space-y-4 pb-4">
            <label className="text-neutrals-900 text-xs font-bold pb-1">
              {t('domain.termine.form.wiederholungLabel')}
            </label>
            <div className="flex flex-row items-end gap-4">
              <OccurrenceCountFormField className="flex-1" />
              <RepeatIntervalSelectFormField />
            </div>
            <form.Subscribe
              selector={(state) => state.values.wiederholungsIntervall}>
              {(wiederholungsIntervall) =>
                wiederholungsIntervall === TerminWiederholungsIntervall.MONAT ||
                wiederholungsIntervall === TerminWiederholungsIntervall.JAHR ? (
                  <WeekdayOccurrenceSelectFormField />
                ) : (
                  wiederholungsIntervall ===
                    TerminWiederholungsIntervall.WOCHE && (
                    <WeekdaySelectionDrawer />
                  )
                )
              }
            </form.Subscribe>
            <DatePickerFormField name="wiederholungBis" />
          </div>
          <form.Subscribe selector={(state) => state.values}>
            {(iw) => {
              const wiederholung: IndividuelleWiederholung = {
                occurrenceCount: iw.occurrenceCount ?? 1,
                weekDayOccurrence: iw.wochentagAuftreten,
                wochentage: iw.wochentagAuswahl
                  ? iw.wochentagAuswahl
                  : [getFixedWeekday(dayjs(iw.datum))],
                wiederholungsIntervall:
                  iw.wiederholungsIntervall ??
                  TerminWiederholungsIntervall.WOCHE,
              };
              return (
                <div className="text-xs text-neutrals-700 font-normal">
                  <IndividuellerWiederholungsText
                    wiederholung={wiederholung}
                    wiederholungBis={iw.wiederholungBis}
                    datum={iw.datum}
                  />
                </div>
              );
            }}
          </form.Subscribe>
        </ScrollArea>
        <div className="p-4 shrink-0">
          <DrawerClose asChild>
            <Button className="w-full" size="lg">
              {t('domain.termine.form.fertig')}
            </Button>
          </DrawerClose>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
